import Box from "@mui/material/Box";
import { ReactNode, RefObject, useEffect, useState } from "react";

type APStickyHeaderProps = {
  children: ReactNode;
  anchorElement: RefObject<HTMLElement>;
  zIndex?: number;
};

const Z_INDEX_STICKY_HEADER = 12;

const APStickyHeader = ({
  children,
  anchorElement,
  zIndex = Z_INDEX_STICKY_HEADER,
}: APStickyHeaderProps) => {
  // Add state to track vertical scroll and header visibility
  const [shouldShowStickyHeader, setShouldShowStickyHeader] = useState(false);

  // Add effect to track header row position and determine sticky header visibility
  useEffect(() => {
    const updateHeaderVisibility = () => {
      if (anchorElement.current) {
        const rect = anchorElement.current.getBoundingClientRect();

        // Show sticky header when the top of the header row is out of view (above the viewport)
        const isTopOutOfView = rect.top < -8;

        setShouldShowStickyHeader(isTopOutOfView);
      }
    };

    // Update on scroll and resize
    updateHeaderVisibility();
    window.addEventListener("scroll", updateHeaderVisibility);
    window.addEventListener("resize", updateHeaderVisibility);

    return () => {
      window.removeEventListener("scroll", updateHeaderVisibility);
      window.removeEventListener("resize", updateHeaderVisibility);
    };
  }, [anchorElement.current]);

  const renderStickyHeader = () => {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          width: "900px",
          zIndex,
          backgroundColor: "white",
          borderBottom: "1px solid",
          borderColor: "outlineVariant.main",
          display: "flex",
          marginLeft: {
            xs: "-16px",
            sm: "-24px",
            md: "-32px",
          },
          paddingRight: {
            xs: "16px",
            sm: "24px",
            md: "32px",
          },
          py: 1,
          maxWidth: "100vw",
          visibility: shouldShowStickyHeader ? "visible" : "hidden",
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            width: "100%",
            marginLeft: {
              xs: "16px",
              sm: "24px",
              md: "32px",
            },
          }}
        >
          {children}
        </Box>
      </Box>
    );
  };

  return <>{renderStickyHeader()}</>;
};

export default APStickyHeader;
